<template>
  <div>
    <h4 class="text--medium text-text-primary font-weight-medium mb-2">
      {{ $t('project.members.MemberSubscription.title') }}
    </h4>

    <i18n-t v-if="userFullNameAndMail && !multi"
            class="text-text-secondary"
            keypath="project.members.MemberSubscription.info"
            tag="p"
            scope="global"
    >
      <template #user>
        <span class="font-weight-bold">
          {{ userFullNameAndMail }}
        </span>
      </template>
      <template #subName>
        <span class="font-weight-bold">
          {{ errorData.name }}
        </span>
      </template>
    </i18n-t>
    <p v-if="multi"
       v-t="{
          path: 'project.members.MemberSubscription.infoMulti',
          plural: errorData.nbUsers,
          args: { nbUsers: errorData.nbUsers, subName: errorData.name },
        }"
       class="text-text-secondary"
    />

    <div class="subscription-options mt-4">
      <AppCard v-if="errorData && errorData.yearlyPrice"
               hover
               elevation="0"
               class="card-option text-text-secondary"
               :class="{'card-option--selected': selectedOption === options.yearlyLicense}"
               @click="selectedOption = options.yearlyLicense"
      >
        {{$t('project.members.MemberSubscription.annualSub', {price: errorData.yearlyPrice})}}
      </AppCard>
      <AppCard v-if="errorData && errorData.monthlyPrice"
               hover
               elevation="0"
               class="card-option text-text-secondary"
               :class="{'card-option--selected': selectedOption === options.monthlyLicense}"
               @click="selectedOption = options.monthlyLicense"
      >
        <template v-if="errorData.monthlyPrice === -1">
          {{$t('project.members.MemberSubscription.freeTrial')}}
        </template>
        <template v-else>
          {{$t('project.members.MemberSubscription.monthlySub', {price: errorData.monthlyPrice})}}
        </template>
      </AppCard>
      <AppCard hover
               elevation="0"
               class="card-option text-text-secondary"
               :class="{'card-option--selected': selectedOption === options.noLicense}"
               @click="selectedOption = options.noLicense"
      >
        {{$t('project.members.MemberSubscription.noSub')}}
      </AppCard>
    </div>
  </div>
</template>

<script>
import AppCard from '@/common/AppCard.vue'
import { toast } from '@/common/utils/toast'

export default {
  name: 'MemberSubscription',
  components: { AppCard },
  props: {
    errorData: {
      type: Object,
      required: true,
    },
    memberForm: {
      type: Object,
      default: () => ({}),
    },
    multi: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['onSubscriptionValidate', 'onSubscriptionDecline', 'update:okDisabled'],
  data () {
    return {
      selectedOption: null,
      options: {
        yearlyLicense: 1,
        monthlyLicense: 2,
        noLicense: 3,
      },
    }
  },
  computed: {
    userFullNameAndMail () {
      return `${this.memberForm.firstName} ${this.memberForm.lastName} (${this.memberForm.email})`
    },
    userFullName () {
      return `${this.memberForm.firstName} ${this.memberForm.lastName}`
    },
    okDisabled () {
      return !this.selectedOption
    },
  },
  watch: {
    // The confirm button lives in the modal footer, owned by the parent dialog, so the
    // parent has to be told when the selection makes it actionable.
    okDisabled: {
      handler (isDisabled) {
        this.$emit('update:okDisabled', isDisabled)
      },
      immediate: true,
    },
  },
  methods: {
    validate () {
      switch (this.selectedOption) {
        case 1 :
          this.$emit('onSubscriptionValidate', { confirmYearly: true })
          break
        case 2 :
          this.$emit('onSubscriptionValidate', { confirmMonthly: true })
          break
        case 3 :
          if (this.multi) {
            toast.error(this.$t('project.members.MemberSubscription.noSubErrorMulti', this.errorData.nbUsers))
          } else {
            toast.error(this.$t('project.members.MemberSubscription.noSubError', { member: this.userFullName }))
          }

          this.$emit('onSubscriptionDecline')
          break
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import "vuetify/settings";

  // Replaces a v-row/v-col grid: grid items stretch by default, so the options keep equal
  // heights without h-100, and this drops the row's -12px margins and the cols' 12px padding,
  // which inflated the body height well past what the content looked like.
  .subscription-options {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;

    @media #{map-get($display-breakpoints, 'md-and-up')} {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  .card-option {
    border: 3px solid transparent;
  }
  .card-option--selected {
    border-color: rgb(var(--v-theme-primary));
  }
</style>
